<template>
    <ListFrame
        :no-results="resultListEmpty"
        :loading="initialMechanicsLoading"
        :error="error"
        :small-content-container="false"
        large-content-container
        :no-results-error-message="noResultsErrorMessage"
        :total="total"
    >
        <template #filter="{ isMobile }">
            <ListFilterUrlSync
                v-model:selected="selected"
                sort-save-key="mechanic_sort"
                :fetch-aggregation="fetchAggregation"
                :facet-collection-id="FacetCollection.parts"
                :is-mobile-view="isMobile"
                :more-menu-entries="menuEntries"
                :sort-order-entries="sortOrderItems"
                :product="productID"
                :hide-facets="!!blobId"
                @on-sort-selection-changed="onChangeSortOrder"
                @on-open-filter="onOpenFilterHandler"
            />
        </template>
        <template #content>
            <Table
                data-qs="mechanic-articles-list-table"
                :q-data-source="qDataSource"
                :no-data-text="noResultsErrorMessage"
                :has-more-results="hasMoreResults"
                :custom-sticky-right-cell-column-name="ADD_TO_COLLECTION_COL_NAME"
                :custom-cells="[QIT_PREVIEW_IMAGE_COLUMN]"
                :mobile-action-column-name="ADD_TO_COLLECTION_COL_NAME"
                :link-to="(row) => routeToMechanicArticleRedirect(row.id)"
                sticky-last-column
                @on-load-more="loadMoreArticles()"
            >
                <template #custom-cell-column-name="{ row }">
                    <AddToCollectionButton
                        class="add-article-to-collection-button"
                        color="secondary"
                        size="sm"
                        :data-qs="`add-article-to-collection-button-${row.id}`"
                        @click="openCollectionOverlay(row)"
                    />
                </template>
                <template #mobile-action="{ row }">
                    <div class="row flex-grow justify-end">
                        <AddToCollectionButton
                            class="add-article-to-collection-button"
                            size="sm"
                            color="secondary"
                            :data-qs="`add-article-to-collection-button-${row.id}`"
                            @click="openCollectionOverlay(row)"
                        />
                    </div>
                </template>
            </Table>
        </template>
        <template v-if="blobId" #no-results>
            <NoResultsError />
        </template>
    </ListFrame>
    <CollectionArticleOverlay
        v-if="articleModelOverlay"
        :article="articleModelOverlay"
        :product-id="product"
        :asset-id="asset"
        @on-close="closeCollectionsOverlay"
        @on-save-succeeded="closeCollectionsOverlay"
    />
</template>

<script setup lang="ts">
/* Komponente, die eine Liste von einbauortspezifischen Artikeln anzeigt. */
import CollectionArticleOverlay, {
    ArticleModelOverlay,
} from "@/abilities/collections/components/overlay/collection-article-overlay.vue";
import { ADD_TO_COLLECTION_COL_NAME } from "@/abilities/mechanic/pages/article-search/articles-list-data-source.ts";
import { useMechanicArticlesDataSource } from "@/abilities/mechanic/pages/article-search/mechanic-articles-data-source";
import NoResultsError from "@/abilities/mechanic/pages/components/no-results-error.vue";
import { useMechanicListMenuEntries } from "@/abilities/mechanic/pages/mechanic-list-meu-entries.ts";
import AddToCollectionButton from "@/shared/collections/add-to-collection-button.vue";
import { QIT_PREVIEW_IMAGE_COLUMN, QTableRowType, resolveCellText } from "@/shared/components/table/q-data-source";
import Table from "@/shared/components/table/table.vue";
import { useAbilityRoute } from "@/shared/environment/ability-route";
import { CommonRouteAliases } from "@/shared/environment/common-route-aliases";
import { useSelectedFacets } from "@/shared/facets/composables/selected-facets";
import { Facet, FacetCollection } from "@/shared/facets/facets.model";
import ListFilterUrlSync from "@/shared/list-filter/list-filter-renderer-url-sync.vue";
import ListFrame from "@/shared/list-frame/list-frame.vue";
import { useScreenSize } from "@/shared/screen/composables/screen-size.ts";
import ShareMenuEntry from "@/shared/share/share-menu-entry.vue";
import { computed, ref, toRefs, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useMechanicArticlesListSort } from "./mechanic-articles-list.model";
import { useMatomo } from "@/shared/analytics/matomo.ts";

const props = defineProps<{
    fullTextSearchValue: string;
    product: string;
    asset?: string;
    blobId?: string;
    isSearch?: boolean;
}>();

const articleModelOverlay = ref<ArticleModelOverlay | undefined>();
const facet = ref<Facet | undefined>();

const { product, asset, blobId } = toRefs(props);

const { isXSmall } = useScreenSize();
const { getPathToChildrenWithoutParents } = useAbilityRoute();
const { menuEntries: mechanicListMenuEntries } = useMechanicListMenuEntries();
const { trackSearch } = useMatomo();

const productID = computed(() => {
    if (!asset?.value && product?.value) return product?.value;

    return undefined;
});

const fullTextSearchValue = computed(() => {
    return props.fullTextSearchValue;
});

const { sorting, onChangeSortOrder, selected } = useSelectedFacets();

const {
    qDataSource,
    loading,
    error,
    resultListEmpty,
    hasMoreResults,
    loadMoreArticles,
    total,
    fetchAggregation,
    mechanicArticlesResult,
} = useMechanicArticlesDataSource(
    computed(() => {
        return { text: fullTextSearchValue.value, product: props.product, asset: props.asset };
    }),
    selected,
    sorting,
    facet,
    blobId
);

const initialMechanicsLoading = computed(() => {
    return !mechanicArticlesResult.value;
});

const { t } = useI18n({ useScope: "global" });

const noResultButFiltersAreSet = ref(false);

watch(loading, () => {
    noResultButFiltersAreSet.value = resultListEmpty.value && selected.value.length > 0;
});

watch([total, fullTextSearchValue.value], () => {
    if (fullTextSearchValue.value && !loading.value) {
        trackSearch(fullTextSearchValue.value, "articles", total.value?.count);
    }
});

const noResultsErrorMessage = computed(() => {
    if (noResultButFiltersAreSet.value) {
        return t("mechanic.No articles found with the set filters Adjust the filter criteria to get more results");
    }
    if (props.fullTextSearchValue) {
        return t("shared.No search results found");
    } else {
        return t("mechanic.No articles exist");
    }
});

function routeToMechanicArticleRedirect(mechanicNodeId: string) {
    return getPathToChildrenWithoutParents([
        {
            alias: CommonRouteAliases.product,
            namedParams: { [CommonRouteAliases.product]: props.product },
        },
        ...(props.asset
            ? [
                  {
                      alias: CommonRouteAliases.asset,
                      namedParams: { [CommonRouteAliases.asset]: props.asset },
                  },
              ]
            : []),
        {
            alias: CommonRouteAliases.searchToMechanicArticle,
            namedParams: { [CommonRouteAliases.searchToMechanicArticle]: mechanicNodeId },
        },
    ]);
}

const { sortOrderItems } = useMechanicArticlesListSort();

const onOpenFilterHandler = (facetProp: Facet) => {
    facet.value = facetProp;
};

const openCollectionOverlay = (cell: QTableRowType) => {
    if (articleModelOverlay.value?.mechanicId === cell.id) {
        closeCollectionsOverlay();
        return;
    }

    articleModelOverlay.value = {
        mechanicId: cell.id,
        title: resolveCellText(cell.title) || cell.id,
    };
};

const closeCollectionsOverlay = () => {
    articleModelOverlay.value = undefined;
};

const menuEntries = computed(() => {
    if (!isXSmall.value) return [];

    return [
        ...mechanicListMenuEntries.value,
        {
            id: "share-entry",
            component: {
                getComponent: () => ShareMenuEntry,
            },
        },
    ];
});
</script>

<style lang="scss" scoped>
.mechanic-articles-cell {
    padding: 0;
    align-items: center;

    a {
        width: 100%;
        height: 100%;
        align-items: center;
        margin: 0;
        padding-left: $spacing-l;
        padding-right: $spacing-l;
    }
}

:deep(.add-article-to-collection-button) {
    max-height: 30px;
}
</style>
